import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import styled from '@emotion/styled';
import {
    RangePicker,
    Form,
    FormInputTextarea,
    useForm,
    MultiSelectButton,
} from '@modernhealth/vital-components';

import { NumbersRowInput } from 'src/shared/components/NumbersRowInput';

import { TextString, getTextAsString } from './ReviewHelpers';

export const QUESTION_LABEL_ID = 'review-question-label';

export enum QuestionFormat {
    MULTI_SELECT = 'multi-select',
    SLIDER = 'slider',
    NUMBERS_ROW = 'numbers-row',
    TEXTAREA = 'textarea',
}

export enum QuestionDisplayField {
    HEADER = 'header',
    TITLE = 'title',
    SUBTITLE = 'subtitle',
}

export const DEFAULT_DISPLAY_ORDER = [
    QuestionDisplayField.HEADER,
    QuestionDisplayField.TITLE,
    QuestionDisplayField.SUBTITLE,
];

interface QuestionBase<K, _> {
    key: K;
    title: TextString;
    subtitle?: TextString;
    displayOrder?: QuestionDisplayField[];
}

export interface MultiSelect<K, V = string[]> extends QuestionBase<K, V> {
    format: QuestionFormat.MULTI_SELECT;
    options: { value: string; label: TextString }[];
}

export interface Slider<K, V = number> extends QuestionBase<K, V> {
    format: QuestionFormat.SLIDER;
    inputRange: [number, number];
    minLabel: TextString;
    maxLabel: TextString;
}

export interface NumbersRow<K, V = { value?: number; feedback?: string }>
    extends QuestionBase<K, V> {
    format: QuestionFormat.NUMBERS_ROW;
    inputRange: [number, number];
    minLabel: TextString;
    maxLabel: TextString;
    feedback: boolean;
    feedbackHint?: TextString;
}

export interface Textarea<K, V = string> extends QuestionBase<K, V> {
    format: QuestionFormat.TEXTAREA;
    hint: TextString;
}

// Ideally we don't need to create two unions for questions manually; however,
// you can't extract the value type from Question union. This is fastest way I
// could sort out to accomplish what I needed in the timeframe we have. This
// can be revisited in the future to try and consolidate it.
export type Question<K = string> = MultiSelect<K> | Slider<K> | NumbersRow<K> | Textarea<K>;
export type ValueTypeFromQuestion<D> = D extends
    | MultiSelect<any, infer P> // eslint-disable-line @typescript-eslint/no-explicit-any
    | Slider<any, infer P> // eslint-disable-line @typescript-eslint/no-explicit-any
    | NumbersRow<any, infer P> // eslint-disable-line @typescript-eslint/no-explicit-any
    | Textarea<any, infer P> // eslint-disable-line @typescript-eslint/no-explicit-any
    ? P
    : undefined;

export function getQuestionState<Q>(
    _: Q,
    state: ValueTypeFromQuestion<Question<any>> | undefined, // eslint-disable-line @typescript-eslint/no-explicit-any
) {
    return state as ValueTypeFromQuestion<Q> | undefined;
}

function TextareaInput<K>({
    question,
    state,
    onStateChange,
}: {
    question: Textarea<K>;
    state?: ValueTypeFromQuestion<Textarea<K>>;
    onStateChange: (v: ValueTypeFromQuestion<Textarea<K>>) => void;
}) {
    const { t } = useTranslation();
    const formDefinition = useForm<{
        field: ValueTypeFromQuestion<Textarea<K>>;
    }>({
        defaultValues: {
            field: state,
        },
    });

    const formValue = formDefinition.watch('field');

    useEffect(() => {
        onStateChange(formValue);
    }, [formValue, onStateChange]);

    return (
        <Form definition={formDefinition}>
            <FormInputTextarea
                name="field"
                placeholder={getTextAsString(t, question.hint)}
                rows={8}
                labelledBy={QUESTION_LABEL_ID}
            />
        </Form>
    );
}

function NumbersRowInputWithFeedback<K>({
    question,
    state,
    onStateChange,
}: {
    question: NumbersRow<K>;
    state?: ValueTypeFromQuestion<NumbersRow<K>>;
    onStateChange: (v: ValueTypeFromQuestion<NumbersRow<K>>) => void;
}) {
    const { t } = useTranslation();
    const formDefinition = useForm<{
        field: ValueTypeFromQuestion<NumbersRow<K>>['feedback'];
    }>({
        defaultValues: {
            field: state?.feedback,
        },
    });

    const feedbackValue = formDefinition.watch('field');

    useEffect(() => {
        onStateChange({ value: state?.value, feedback: feedbackValue });
        // We do not want this to fire if state?.value has changed. This is
        // wonky but it is because of how we must to use the watch APIs
        // with vita forms. Change handlers aren't supported for now.
    }, [feedbackValue, onStateChange]); // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <>
            <NumbersRowInput
                range={question.inputRange}
                value={state?.value}
                onValueChange={v => onStateChange({ value: v, feedback: state?.feedback })}
                minLabel={getTextAsString(t, question.minLabel)}
                maxLabel={getTextAsString(t, question.maxLabel)}
            />
            {question.feedback && state?.value !== undefined && (
                <Form definition={formDefinition}>
                    <FormInputTextarea
                        name="field"
                        placeholder={
                            question.feedbackHint
                                ? getTextAsString(t, question.feedbackHint)
                                : undefined
                        }
                        rows={8}
                        labelledBy={QUESTION_LABEL_ID}
                    />
                </Form>
            )}
        </>
    );
}

export function QuestionInput<K>({
    question,
    state,
    onStateChange,
}: {
    question: Question<K>;
    state?: ValueTypeFromQuestion<Question<K>>;
    onStateChange: (v: ValueTypeFromQuestion<Question<K>>) => void;
}) {
    const { t } = useTranslation();

    if (question.format === QuestionFormat.SLIDER) {
        const questionState = getQuestionState(question, state);
        const [_, rangeMax] = question.inputRange;

        return (
            <RangePicker
                labelledBy={QUESTION_LABEL_ID}
                // vita doesn't support setting a mininum range so we will
                // ignore this for now.
                // rangeMin={rangeMin}
                range={rangeMax}
                minLabel={getTextAsString(t, question.minLabel)}
                maxLabel={getTextAsString(t, question.maxLabel)}
                onValueChange={onStateChange}
                value={questionState ?? undefined}
                css={{ width: '100%' }}
            />
        );
    }

    if (question.format === QuestionFormat.NUMBERS_ROW) {
        const questionState = getQuestionState(question, state);

        return (
            <NumbersRowInputWithFeedback
                question={question}
                state={questionState}
                onStateChange={onStateChange}
            />
        );
    }

    if (question.format === QuestionFormat.MULTI_SELECT) {
        const questionState = getQuestionState(question, state);

        return (
            <AnswerTileContainer>
                {question.options.map(o => {
                    const checked = !!questionState?.find(v => v === o.value);
                    return (
                        <MultiSelectButton
                            key={o.value}
                            text={getTextAsString(t, o.label)}
                            onClick={() => {
                                const localState = questionState ?? [];
                                if (checked) {
                                    onStateChange(localState.filter(a => a !== o.value));
                                } else {
                                    onStateChange([...localState, o.value]);
                                }
                            }}
                            active={checked}
                            css={{
                                width: '48.5%',
                            }}
                        />
                    );
                })}
            </AnswerTileContainer>
        );
    }

    if (question.format === QuestionFormat.TEXTAREA) {
        const questionState = getQuestionState(question, state);

        return (
            <TextareaInput
                question={question}
                state={questionState}
                onStateChange={onStateChange}
            />
        );
    }

    return null;
}

const AnswerTileContainer = styled.div`
    display: flex;
    flex-wrap: wrap;
    justify-content: space-between;
`;
